import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  InputAdornment
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { inviteToOrJoinClass } from '../service/invite.service';
import { getCke } from '../helper/cookieHelper';
import Modal from './common/Modal';

function AddPopup({
  popUpType = 'addClass',
  handleClosePopup,
  isOpen,
  title,
  studentClassList,
  fetchNextData,
  isClassLoading,
  hasMore,
  addedPopupRes,
  selectedClassId
}) {
  const [open, setOpen] = useState(isOpen);
  // state for new student
  const [studentOrTeacherName, setStudentOrTeacherName] = useState('');
  const [studentOrTeacherPhone, setStudentOrTeacherNumber] = useState('');
  const [studentClass, setStudentClass] = useState(selectedClassId);
  const [studentOrTeacherNote, setStudentOrTeacherNote] = useState('');
  const [isBtnLoading, setBtnLoading] = useState(false);

  const [studentNameError, setStudentNameError] = useState(false);
  const [studentNumberError, setStudentNumberError] = useState(false);
  const [classSelectError, setClassSelectError] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setOpen(isOpen);
    if (isOpen === false) {
      // Clear student data
      setStudentOrTeacherName('');
      setStudentOrTeacherNumber('');
      setStudentClass(selectedClassId);
      setStudentOrTeacherNote('');

      setStudentNameError(false);
      setStudentNumberError(false);
      setClassSelectError(false);
    }
  }, [isOpen]);

  const handleInvite = async (obj) => {
    setBtnLoading(true);
    const res = await inviteToOrJoinClass(obj);
    if (res) {
      enqueueSnackbar('Student invitation sent', { variant: 'success' });
      setBtnLoading(false);
      addedPopupRes(res);
      handleClosePopup();
    } else {
      setBtnLoading(false);
    }
  };

  const handleSetStudent = async () => {
    // Student validation
    const nameValidation = yup.string().required();
    const numberValidation = yup.number().required().positive().integer();
    let isNameValid;
    let isNumberValid;
    let isClassValid;
    // validate name
    await nameValidation.isValid(studentOrTeacherName).then((isValid) => {
      isNameValid = isValid;
      if (!isValid) setStudentNameError(true);
      else setStudentNameError(false);
    });

    // validate number
    await numberValidation.isValid(studentOrTeacherPhone).then((isValid) => {
      isNumberValid = isValid;
      if (!isValid) setStudentNumberError(true);
      else setStudentNumberError(false);
    });

    await nameValidation.isValid(studentClass).then((isValid) => {
      isClassValid = isValid;
      if (!isValid) setClassSelectError(true);
      else setClassSelectError(false);
    });

    if (isNameValid && isNumberValid && isClassValid) {
      const obj = {
        role: popUpType,
        source: 'invite',
        phoneNumber: studentOrTeacherPhone,
        fullName: studentOrTeacherName,
        classId: studentClass,
        orgId: studentClassList.find((sc) => sc.id === studentClass).orgId._id,
        isDirectlyAdded: true,
        requesterUserId: getCke('userId')
      };
      if (studentOrTeacherNote.trim() !== '') obj.requesterNotes = studentOrTeacherNote;
      handleInvite(obj);
    }
  };

  // Load more classes to display
  const loadMoreItems = (event) => {
    if (event.target.scrollTop === event.target.scrollHeight - 200 && hasMore && !isClassLoading) {
      fetchNextData();
    }
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClosePopup}
        title={title}
        cancelText="Cancel"
        actionText="Add"
        onAction={() => handleSetStudent()}
        onCancel={() => handleClosePopup()}
        isBtnLoading={isBtnLoading}
        isCancelDisabled={isBtnLoading}
      >
        <TextField
          autoFocus
          error={studentNameError}
          margin="dense"
          id="student-name"
          label="Name *"
          type="text"
          fullWidth
          name="studentOrTeacherName"
          value={studentOrTeacherName}
          onChange={(e) => setStudentOrTeacherName(e.target.value)}
          helperText={studentNameError ? 'Name is requier' : ''}
        />
        <TextField
          margin="dense"
          id="contact-number"
          error={studentNumberError}
          label="Number *"
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">+91</InputAdornment>,
            maxLength: 10
          }}
          fullWidth
          onInput={(e) => {
            if (e.target.value !== '') {
              /* eslint radix: ["error", "as-needed"] */
              e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 10);
            }
          }}
          name="studentOrTeacherPhone"
          value={studentOrTeacherPhone === '' ? '' : studentOrTeacherPhone}
          onChange={(e) => {
            setStudentOrTeacherNumber(e.target.value);
          }}
          helperText={studentNumberError ? 'Number is required' : ''}
        />
        <FormControl margin="dense" fullWidth>
          <InputLabel id="demo-multiple-name-label">Select Class</InputLabel>
          <Select
            fullWidth
            labelId="demo-multiple-name-label"
            id="demo-multiple-name"
            value={studentClass}
            onChange={(e) => setStudentClass(e.target.value)}
            input={<OutlinedInput label="Select Class" />}
            error={classSelectError}
            MenuProps={{
              PaperProps: {
                onScroll: loadMoreItems,
                style: {
                  maxHeight: 200,
                  overflowY: 'auto'
                }
              }
            }}
          >
            {studentClassList.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          fullWidth
          id="student-notes"
          label="Notes"
          name="studentOrTeacherNote"
          multiline
          maxRows={4}
          value={studentOrTeacherNote}
          onChange={(e) => setStudentOrTeacherNote(e.target.value)}
        />
      </Modal>
    </div>
  );
}

AddPopup.defaultProps = {
  addedPopupRes: () => {},
  fetchNextData: () => {},
  hasMore: false,
  selectedClassId: ''
};

AddPopup.propTypes = {
  handleClosePopup: PropTypes.func.isRequired,
  popUpType: PropTypes.string,
  title: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  studentClassList: PropTypes.array,
  fetchNextData: PropTypes.func,
  isClassLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  addedPopupRes: PropTypes.func,
  selectedClassId: PropTypes.string
};

export default AddPopup;
